import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { usePartnerRatings } from '@/hooks/usePartnerRatings';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AddRatingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
}

export function AddRatingDialog({ open, onOpenChange, companyId }: AddRatingDialogProps) {
  const { createRating, weights, calculateOverallRating } = usePartnerRatings(companyId);
  
  const [safetyRating, setSafetyRating] = useState(0);
  const [safetyNotes, setSafetyNotes] = useState('');
  const [qualityRating, setQualityRating] = useState(0);
  const [qualityNotes, setQualityNotes] = useState('');
  const [reliabilityRating, setReliabilityRating] = useState(0);
  const [reliabilityNotes, setReliabilityNotes] = useState('');
  const [overallNotes, setOverallNotes] = useState('');
  const [contractValue, setContractValue] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Only calculate if all ratings are set to avoid validation errors with initial 0 values
  const overallRating = (safetyRating > 0 && qualityRating > 0 && reliabilityRating > 0)
    ? calculateOverallRating?.(safetyRating, qualityRating, reliabilityRating) || 0
    : 0;

  const StarRating = ({ value, onChange, label }: { value: number; onChange: (val: number) => void; label: string }) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="transition-transform hover:scale-110"
          >
            <Star
              className={cn(
                'h-8 w-8',
                star <= value ? 'fill-primary text-primary' : 'text-muted-foreground'
              )}
            />
          </button>
        ))}
        <span className="ml-2 text-lg font-semibold">{value.toFixed(1)}</span>
      </div>
    </div>
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!safetyRating || !qualityRating || !reliabilityRating) {
      console.error('Missing ratings');
      return;
    }

    // Validate ratings are in valid range (1-5)
    if (safetyRating < 1 || safetyRating > 5 || 
        qualityRating < 1 || qualityRating > 5 || 
        reliabilityRating < 1 || reliabilityRating > 5) {
      console.error('Invalid rating values:', { safetyRating, qualityRating, reliabilityRating });
      return;
    }

    // Detailed logging before submission
    console.log('=== RATING SUBMISSION DEBUG ===');
    console.log('Individual ratings:', { safetyRating, qualityRating, reliabilityRating });
    console.log('Current weights:', weights);
    console.log('Overall rating to be submitted:', overallRating);
    console.log('Contract value:', contractValue);
    console.log('==============================');

    // Client-side check to prevent overflow
    if (overallRating > 5) {
      console.error('OVERFLOW DETECTED! Overall rating exceeds 5:', overallRating);
      alert(`Invalid overall rating: ${overallRating}. This appears to be a calculation error. Please contact support.`);
      return;
    }

    setSubmitting(true);
    try {
      await createRating({
        companyId,
        data: {
          safety_rating: safetyRating,
          safety_notes: safetyNotes || undefined,
          quality_rating: qualityRating,
          quality_notes: qualityNotes || undefined,
          reliability_rating: reliabilityRating,
          reliability_notes: reliabilityNotes || undefined,
          overall_notes: overallNotes || undefined,
          contract_value: contractValue ? parseFloat(contractValue) : undefined,
          rating_period_start: periodStart || undefined,
          rating_period_end: periodEnd || undefined,
        },
      });
      
      // Reset form
      setSafetyRating(0);
      setSafetyNotes('');
      setQualityRating(0);
      setQualityNotes('');
      setReliabilityRating(0);
      setReliabilityNotes('');
      setOverallNotes('');
      setContractValue('');
      setPeriodStart('');
      setPeriodEnd('');
      onOpenChange(false);
    } catch (error) {
      console.error('Rating error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Performance Rating</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="periodStart">Rating Period Start</Label>
              <Input
                id="periodStart"
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="periodEnd">Rating Period End</Label>
              <Input
                id="periodEnd"
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="contractValue">Contract Value ($)</Label>
            <Input
              id="contractValue"
              type="number"
              step="0.01"
              value={contractValue}
              onChange={(e) => setContractValue(e.target.value)}
              placeholder="0.00"
            />
          </div>

          <div className="space-y-6 p-4 rounded-lg bg-muted/50">
            <StarRating
              value={safetyRating}
              onChange={setSafetyRating}
              label="Safety Rating *"
            />
            <Textarea
              value={safetyNotes}
              onChange={(e) => setSafetyNotes(e.target.value)}
              placeholder="Safety performance notes..."
              rows={2}
            />
          </div>

          <div className="space-y-6 p-4 rounded-lg bg-muted/50">
            <StarRating
              value={qualityRating}
              onChange={setQualityRating}
              label="Quality Rating *"
            />
            <Textarea
              value={qualityNotes}
              onChange={(e) => setQualityNotes(e.target.value)}
              placeholder="Quality performance notes..."
              rows={2}
            />
          </div>

          <div className="space-y-6 p-4 rounded-lg bg-muted/50">
            <StarRating
              value={reliabilityRating}
              onChange={setReliabilityRating}
              label="Reliability Rating *"
            />
            <Textarea
              value={reliabilityNotes}
              onChange={(e) => setReliabilityNotes(e.target.value)}
              placeholder="Reliability performance notes..."
              rows={2}
            />
          </div>

          {weights && safetyRating > 0 && qualityRating > 0 && reliabilityRating > 0 && (
            <div className="p-4 rounded-lg bg-primary/10 space-y-2">
              <Label>Overall Rating (Calculated)</Label>
              <div className="flex items-center gap-2">
                <Star className="h-8 w-8 fill-primary text-primary" />
                <span className="text-3xl font-bold">{overallRating.toFixed(2)}</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Formula: ({safetyRating} × {(weights.safety_weight * 100).toFixed(0)}%) + 
                ({qualityRating} × {(weights.quality_weight * 100).toFixed(0)}%) + 
                ({reliabilityRating} × {(weights.reliability_weight * 100).toFixed(0)}%)
              </p>
            </div>
          )}

          <div>
            <Label htmlFor="overallNotes">Overall Notes</Label>
            <Textarea
              id="overallNotes"
              value={overallNotes}
              onChange={(e) => setOverallNotes(e.target.value)}
              placeholder="General comments about this rating period..."
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={submitting || !safetyRating || !qualityRating || !reliabilityRating}
            >
              {submitting ? 'Saving...' : 'Add Rating'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
